import { CommonModule } from '@angular/common';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { Component, OnDestroy, inject, input, signal } from '@angular/core';
import { QuickMessageLauncherService } from '../../quick-message-launcher.service';

const USER_HOVER_CARD_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 10,
  },
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -10,
  },
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 10,
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -10,
  },
  {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
    offsetY: 10,
  },
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 10,
  },
  {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
    offsetY: -10,
  },
  {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
    offsetY: -10,
  },
];

@Component({
  selector: 'app-user-hover-card',
  standalone: true,
  imports: [CommonModule, OverlayModule],
  templateUrl: './user-hover-card.component.html',
  styleUrl: './user-hover-card.component.scss',
})
export class UserHoverCardComponent implements OnDestroy {
  private readonly quickMessageLauncher = inject(QuickMessageLauncherService);
  readonly name = input.required<string>();
  readonly email = input<string | null>(null);
  readonly avatarUrl = input<string | null>(null);
  readonly initials = input.required<string>();
  readonly accent = input('#8a79d6');
  readonly quickMessageUserId = input<string | null>(null);
  readonly canQuickMessage = input(false);
  readonly positions = USER_HOVER_CARD_POSITIONS;
  protected readonly open = signal(false);
  protected readonly draftMessage = signal('');
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnDestroy(): void {
    this.cancelClose();
  }

  protected openCard(): void {
    this.cancelClose();
    this.open.set(true);
  }

  protected scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      this.open.set(false);
      this.closeTimer = null;
    }, 260);
  }

  protected closeCard(): void {
    this.cancelClose();
    this.open.set(false);
    this.draftMessage.set('');
  }

  protected updateDraftMessage(event: Event): void {
    const target = event.target as HTMLTextAreaElement | null;
    this.draftMessage.set(target?.value ?? '');
  }

  protected sendQuickMessage(): void {
    const userId = this.quickMessageUserId()?.trim();
    const message = this.draftMessage().trim();
    if (!userId || !this.canQuickMessage() || !message) {
      return;
    }

    this.quickMessageLauncher.requestQuickMessage({ userId, message });
    this.closeCard();
  }

  protected cancelClose(): void {
    if (!this.closeTimer) {
      return;
    }

    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }
}
